/**
 * shadowState.js — Normalize DeviceShadow state reads.
 *
 * Backend DeviceShadow model (new):
 *   state: {
 *     measurement: { payload, metadata, timestamp },
 *     event:       { payload, metadata, timestamp },
 *     command:     { payload, metadata, timestamp },
 *     configuration: { payload, metadata, timestamp },
 *     stat:        { payload, metadata, timestamp },
 *   }
 *
 * Legacy (frontend still reads):
 *   current_state: { payload, speed_kmh, heading, ... }
 *
 * This utility resolves from either shape, enabling incremental migration.
 */

/**
 * Extract the primary measurement channel from a shadow document.
 * Tries new `state.measurement` first, then legacy `current_state`.
 *
 * @param {Object} shadow — raw shadow document from API / store
 * @returns {{ payload: Object, timestamp: string|null, metadata: Object }}
 */
export function resolveMeasurement(shadow) {
  if (!shadow) return { payload: {}, timestamp: null, metadata: {} }

  // New channel structure
  const m = shadow.state?.measurement
  if (m?.payload) {
    return {
      payload: m.payload,
      timestamp: m.timestamp || shadow.updated_at || null,
      metadata: m.metadata || {},
    }
  }

  // Legacy: current_state with nested payload (Flespi-style)
  const cs = shadow.current_state || {}
  if (cs.payload && typeof cs.payload === 'object') {
    return {
      payload: cs.payload,
      timestamp: cs.timestamp || shadow.updated_at || null,
      metadata: {},
    }
  }

  // Legacy: current_state IS the flat payload
  return {
    payload: cs,
    timestamp: cs.timestamp || shadow.updated_at || null,
    metadata: {},
  }
}

/**
 * Extract event channel data from a shadow document.
 *
 * @param {Object} shadow
 * @returns {{ payload: Object, timestamp: string|null, event_type: string }}
 */
export function resolveEvent(shadow) {
  if (!shadow) return { payload: {}, timestamp: null, event_type: '' }

  // New channel structure
  const e = shadow.state?.event
  if (e?.payload) {
    return {
      payload: e.payload,
      timestamp: e.timestamp || shadow.updated_at || null,
      event_type: e.payload.event_type || e.metadata?.event_type || '',
    }
  }

  // Legacy: event_type on current_state
  const cs = shadow.current_state || {}
  return {
    payload: {},
    timestamp: null,
    event_type: cs.event_type || '',
  }
}

/**
 * Extract per-device measurement payload from shadow.devices map.
 * The `devices` map is keyed by device_role. Each entry has:
 *   { device_id, device_type, payload, timestamp, ... }
 *
 * @param {Object} shadow
 * @returns {{ payload: Object, timestamp: string|null, deviceId: string|null }}
 *   The primary device's measurement data.
 */
export function resolvePrimaryDevicePayload(shadow) {
  const devicesMap = shadow?.devices || {}
  const entries = Object.values(devicesMap)
  // Prefer entry marked is_primary, else first entry
  const primary = entries.find(e => e.is_primary) || entries[0]
  if (!primary) return { payload: {}, timestamp: null, deviceId: null }

  return {
    payload: primary.payload || {},
    timestamp: primary.timestamp || null,
    deviceId: primary.device_id || null,
  }
}

/**
 * Single-call helper that merges device-level payload with shadow-level
 * measurement channel data. Device-level takes precedence.
 *
 * @param {Object} shadow
 * @returns {{ payload: Object, timestamp: string|null }}
 */
export function resolvePayload(shadow) {
  const device = resolvePrimaryDevicePayload(shadow)
  const measurement = resolveMeasurement(shadow)

  // Device-level payload wins if non-empty
  const payload = Object.keys(device.payload).length
    ? device.payload
    : measurement.payload

  const timestamp = device.timestamp || measurement.timestamp

  return { payload, timestamp }
}

/**
 * Extract common telemetry fields from a shadow, normalizing across
 * Flespi-style keys and legacy flat keys.
 *
 * @param {Object} shadow
 * @returns {{ speed: number, heading: number, ignition: any, fuel: any, battery: any, landmark: string|null }}
 */
export function extractTelemetryFields(shadow) {
  const { payload: p } = resolvePayload(shadow)
  const cs = shadow?.current_state || {}

  return {
    speed:    p['position.speed']                  ?? cs.speed_kmh            ?? 0,
    heading:  p['position.direction']              ?? cs.heading              ?? 0,
    ignition: p['engine.ignition.status']          ?? cs.ignition             ?? null,
    fuel:     p['fuel.level.percent']              ?? cs.fuel_level_percent   ?? null,
    battery:  p['battery.voltage'] ?? p['external.powersource.voltage']
                                                   ?? cs.battery_voltage      ?? null,
    landmark: cs.nearest_landmark                  ?? null,
  }
}
